import { useEffect, useRef } from "react";
import { isPerformanceApiAvailable, measure } from "../utils";

function getNavigationMarkName() {
  // The `routeChangeStart` mark comes from Next.js router events.
  const routerNavigationMark = "routeChangeStart";
  // The `navigationStart` mark comes from the Performance API.
  const initialNavigationMark = "navigationStart";

  const hasRouterNavigation =
    window.performance.getEntriesByName(routerNavigationMark, "mark").length >
    0;

  return hasRouterNavigation ? routerNavigationMark : initialNavigationMark;
}

/**
 * Measures time between navigation and some condition being met.
 * Does nothing if the Performance API is not available.
 *
 * Example usage:
 * ```ts
 * const { loading, data } = useSearchExample(text);
 * useTimeSinceNavigation('first-search', !loading);
 * ```
 */
export function useTimeSinceNavigation(
  name: string,
  condition: boolean,
  detail?: Record<keyof any, unknown>
) {
  const hasMeasured = useRef(false);

  useEffect(() => {
    if (!isPerformanceApiAvailable()) {
      return;
    }

    if (condition && !hasMeasured.current) {
      hasMeasured.current = true;

      const fromMark = getNavigationMarkName();

      const measurement = measure(name, {
        detail,
        start: fromMark,
      });

      console.debug(
        `⏱️ ${name} measured ${measurement.duration}ms since navigationStart`
      );
    }
  }, [name, condition]);
}
